/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";

import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";
import { CallbackRef, useForkRef } from "../../hooks/useForkRef";
import * as attributes from "./dragAndDropAttributes";
import { useDragAndDropContext } from "./DragAndDropContext";
import {
	IDraggableElProps,
	IDraggableInfo,
	IDragHandleProps,
	IDragPoint,
	TDraggableID
} from "./DragAndDropTypes";
import { getDraggableInfo, preventHtml5Dnd } from "./utils";
import { useDroppableContext } from "./DroppableContext";
import { useDeepCompareMemo } from "use-deep-compare";

export interface IDraggableProps extends React.ComponentPropsWithRef<"div"> {
	className?: string;
	draggableId: TDraggableID;
	index: number;
	children:
		| React.ReactNode
		| ((props: {
				draggableRef: CallbackRef;
				dragHandleProps: IDragHandleProps;
				draggableProps: IDraggableElProps;
		  }) => JSX.Element);
	disabled?: boolean;
}

const toPx = (value: number) => `${value}px`;

export const Draggable: React.FC<IDraggableProps> = React.forwardRef<HTMLDivElement, IDraggableProps>(
	({ children, className, draggableId, index, disabled, ...props }, ref) => {
		const baseClass = BEM("edt-dnd-draggable");
		const { contextId, registry } = useDragAndDropContext();
		const { droppableId, draggignOver } = useDroppableContext();

		const [ { dragging, moveBy }, setDragState ] = useState<{
			dragging?: IDraggableInfo;
			moveBy?: IDragPoint;
		}>({});

		const style = useDeepCompareMemo<React.CSSProperties>(() => {
			if (dragging) {
				const x = toPx(moveBy?.x || 0);
				const y = toPx(moveBy?.y || 0);
				const top = toPx(dragging.box.contentBox.top);
				const left = toPx(dragging.box.contentBox.left);
				const width = toPx(dragging.box.contentBox.width);
				const height = toPx(dragging.box.contentBox.height);

				return {
					position: "fixed",
					top,
					left,
					width,
					height,
					zIndex: 9999999,
					transform: `translate(${x}, ${y})`,
					pointerEvents: "none",
					userSelect: "none"
				};
			}

			if (moveBy?.x || moveBy?.y) {
				const x = toPx(moveBy?.x || 0);
				const y = toPx(moveBy?.y || 0);
				return {
					transform: `translate(${x}, ${y})`,
					transition: "transform 60ms ease",
					pointerEvents: "none"
				};
			}

			if (draggignOver) {
				return {
					pointerEvents: "none"
				};
			}

			return {};
		}, [ dragging, moveBy, draggignOver ]);

		const draggableRef = useRef<HTMLDivElement>();
		const forkRef = useForkRef(draggableRef, ref);

		const dragHandleProps: IDragHandleProps = useMemo(
			() =>
				!disabled
					? {
							tabIndex: 0,
							role: "button",
							[attributes.dragHandle.draggableId]: draggableId,
							[attributes.dragHandle.contextId]: contextId,
							draggable: false,
							onDragStart: preventHtml5Dnd
					  }
					: null,
			[ contextId, draggableId, disabled ]
		);

		const draggableProps = useMemo<IDraggableElProps>(
			() => ({
				[attributes.draggable.draggableId]: draggableId,
				[attributes.draggable.contextId]: contextId,
				style
			}),
			[ style, draggableId, contextId ]
		);

		useEffect(() => {
			registry.draggable.register({
				id: draggableId,
				droppableId,
				index,
				getInfo: () => {
					return getDraggableInfo({ draggableId, el: draggableRef.current });
				},
				onDragMove: ({ initialPoint, currentPoint, dragging, draggingOver }) => {
					setDragState((prevState) => {
						const movedByX = currentPoint.x - initialPoint.x;
						const movedByY = currentPoint.y - initialPoint.y;

						if (dragging.draggableId === draggableId) {
							return {
								dragging,
								moveBy: {
									x: movedByX,
									y: movedByY
								}
							};
						}

						if (draggingOver === droppableId) {
							const rect = draggableRef.current.getBoundingClientRect();
							const movedY = rect.y - (prevState.moveBy?.y || 0);
							const transitionFinished = rect.y === movedY;
							const middleY =
								rect.y +
								rect.height / 2 -
								(transitionFinished ? prevState.moveBy?.y || 0 : 0);

							const draggedPoint = dragging?.box?.contentBox?.center;

							const newDraggedCenter = {
								x: draggedPoint.x + movedByX,
								y: draggedPoint.y + movedByY
							};

							if (newDraggedCenter.y < middleY || newDraggedCenter.y < rect.y) {
								return {
									dragging: null,
									moveBy: {
										x: 0,
										y: dragging.box.borderBox.height
									}
								};
							}
						}

						return {
							dragging: null,
							moveBy: null
						};
					});

					return;
				},
				onDragDrop: () => {
					setDragState({
						dragging: null,
						moveBy: null
					});
				}
			});

			return () => registry.draggable.unregister(draggableId);
		}, [ draggableId, droppableId, index ]);

		if (isFunction(children)) {
			return children({ draggableRef: forkRef, draggableProps, dragHandleProps }) as JSX.Element;
		}

		if (className) {
			baseClass.add(className);
		}

		return (
			<div
				className={baseClass.toClassName()}
				ref={forkRef}
				{...props}
				{...dragHandleProps}
				{...draggableProps}
			>
				{children}
			</div>
		);
	}
);
